import { useState } from 'react';
import { Avatar, Box, Dialog, IconButton, Tooltip, Typography, Stack } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { DataTable } from '../../components/common/DataTable';
import { StatusChip } from '../../components/common/StatusChip';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { PageHeader, PrimaryActionButton } from '../../components/common/PageHeader';
import { CustomerForm } from '../../components/forms/CustomerForm';
import { customerService } from '../../services/customer.service';
import type { Customer, CreateCustomerForm } from '../../types';

export function CustomersPage() {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Customer | undefined>();
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['customers', page, rowsPerPage],
    queryFn: () => customerService.getAll({ page: page + 1, limit: rowsPerPage }),
  });

  const getErrorMessage = (err: unknown, fallback: string): string => {
    const e = err as { response?: { data?: { message?: string } }; message?: string };
    return e.response?.data?.message || e.message || fallback;
  };

  const createMutation = useMutation({
    mutationFn: (payload: CreateCustomerForm) =>
      editing ? customerService.update(editing.id, payload) : customerService.create(payload),
    onSuccess: () => {
      toast.success(editing ? 'Customer updated' : 'Customer created');
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['geofences'] });
      setOpen(false);
      setEditing(undefined);
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err, 'Failed to save customer')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => customerService.delete(id),
    onSuccess: () => {
      toast.success('Customer removed');
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setDeleteId(null);
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err, 'Failed to delete customer')),
  });

  const columns = [
    {
      id: 'name',
      label: 'Customer',
      minWidth: 200,
      render: (row: Customer) => (
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              fontSize: '0.8rem',
              fontWeight: 700,
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
              color: '#1D4ED8',
            }}
          >
            {row.name.slice(0, 2).toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }} noWrap>
              {row.name}
            </Typography>
            {row.contactPerson && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {row.contactPerson}
              </Typography>
            )}
          </Box>
        </Stack>
      ),
    },
    { id: 'mobile', label: 'Mobile', render: (row: Customer) => row.mobile ?? '—' },
    {
      id: 'address',
      label: 'Address',
      minWidth: 200,
      render: (row: Customer) => (
        <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
          {row.address ?? '—'}
        </Typography>
      ),
    },
    { id: 'status', label: 'Status', render: (row: Customer) => <StatusChip status={row.status} /> },
    {
      id: 'actions',
      label: '',
      align: 'right' as const,
      render: (row: Customer) => (
        <Box display="flex" gap={0.5} justifyContent="flex-end">
          <Tooltip title="Edit customer">
            <IconButton
              size="small"
              aria-label={`Edit ${row.name}`}
              onClick={() => {
                setEditing(row);
                setOpen(true);
              }}
            >
              <EditRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete customer">
            <IconButton
              size="small"
              color="error"
              aria-label={`Delete ${row.name}`}
              onClick={() => setDeleteId(row.id)}
            >
              <DeleteOutlineRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        compact
        title="Customers"
        subtitle="Customer directory, locations, and geofence setup"
        action={
          <PrimaryActionButton
            label="Add Customer"
            icon={<AddRoundedIcon />}
            onClick={() => {
              setEditing(undefined);
              setOpen(true);
            }}
          />
        }
      />

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        getRowId={(row) => row.id}
        isLoading={isLoading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={data?.total ?? 0}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        emptyMessage="No customers yet"
        emptySubtext="Add your first customer with a location to enable visits and geofencing."
      />

      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
          setEditing(undefined);
        }}
        maxWidth="md"
        fullWidth
      >
        <CustomerForm
          customer={editing}
          onSubmit={async (payload) => {
            await createMutation.mutateAsync(payload);
          }}
          onCancel={() => {
            setOpen(false);
            setEditing(undefined);
          }}
          isSubmitting={createMutation.isPending}
        />
      </Dialog>

      <ConfirmDialog
        open={!!deleteId}
        title="Delete customer"
        message="This will remove the customer and related geofence. Continue?"
        onConfirm={() => deleteId && deleteMutation.mutate(deleteId)}
        onCancel={() => setDeleteId(null)}
        confirmLabel="Delete"
        confirmColor="error"
      />
    </Box>
  );
}
